import type {
    homeFeedQueriesInspectionQuery as InspectionQuery,
} from '../relay/__generated__/homeFeedQueriesInspectionQuery.graphql';

/**
 * Plain view models for the wildcards, results-changed and engagement sections of the Home feed panel
 * (PRD pv-platform#156). Enum-valued fields stay strings so an unknown future value shows as given.
 */
export interface WildcardPacingView {
    /** Raw signals: how many wildcards were shown in the look-back (not homes). */
    timesShown: number;
    lookbackDays: number;
    rate: number;
    oneIn: number | null;
    baseRate: number;
    rateState: string;
    activeAxes: readonly string[];
    /** How many of the most recent outcomes the rate weighs. */
    rateWindow: number;
    paused: readonly { axis: string; untilUtc: string }[];
    recent: readonly { axis: string; skipped: boolean; atUtc: string }[];
}

export interface WildcardAxisOffView {
    axis: string;
    source: string;
    sinceUtc: string;
}

export interface PriceFirmView {
    isFirm: boolean;
    answerKey: string;
    answer: string;
    answeredUtc: string | null;
}

export interface HomeFeedWildcardsView {
    planState: string;
    notPlannedReason: string | null;
    pacing: WildcardPacingView;
    axesOff: readonly WildcardAxisOffView[];
    priceFirm: PriceFirmView;
}

export interface ResultsMoverView {
    listingId: string;
    address: string;
    direction: string;
    rank: number;
    previousRank: number;
    places: number;
    score: number;
    reason: string;
    fitBand: string;
}

export interface ResultsChangedView {
    pending: string;
    /** What the member's next read would do to the baseline; NONE / null = nothing. */
    readWouldWrite: string | null;
    card: { title: string; movers: readonly ResultsMoverView[] } | null;
    state: string;
    baselineMovedUtc: string | null;
    cardNotedSinceUtc: string | null;
    cardLapsesUtc: string | null;
    preferencesChangedSinceBaseline: boolean | null;
}

export interface EngagementEventView {
    kind: string;
    occurredUtc: string;
    listingId: string;
    quantity: number | null;
    wildcardAxis: string | null;
}

export interface HomeFeedEngagementView {
    windowDays: number;
    cap: number;
    truncated: boolean;
    /** Newest first. */
    events: readonly EngagementEventView[];
}

type Payload = InspectionQuery['response']['homeFeedInspection'];

export function mapWildcards(w: Payload['wildcards']): HomeFeedWildcardsView | null {
    if (!w) return null;
    return {
        planState: w.planState,
        notPlannedReason: w.notPlannedReason ?? null,
        pacing: {
            timesShown: w.pacing.timesShown,
            lookbackDays: w.pacing.lookbackDays,
            rate: w.pacing.rate,
            oneIn: w.pacing.oneIn ?? null,
            baseRate: w.pacing.baseRate,
            rateState: w.pacing.rateState,
            activeAxes: [...w.pacing.activeAxes],
            rateWindow: w.pacing.rateWindow,
            paused: w.pacing.paused.map((p) => ({ axis: p.axis, untilUtc: p.untilUtc })),
            recent: w.pacing.recent.map((r) => ({ axis: r.axis, skipped: r.skipped, atUtc: r.atUtc })),
        },
        axesOff: w.axesOff.map((a) => ({ axis: a.axis, source: a.source, sinceUtc: a.sinceUtc })),
        priceFirm: {
            isFirm: w.priceFirm.isFirm,
            answerKey: w.priceFirm.answerKey,
            answer: w.priceFirm.answer,
            answeredUtc: w.priceFirm.answeredUtc ?? null,
        },
    };
}

export function mapResultsChanged(r: Payload['resultsChanged']): ResultsChangedView | null {
    if (!r) return null;
    return {
        pending: r.pending,
        readWouldWrite: r.readWouldWrite ?? null,
        card: r.card
            ? {
                  title: r.card.title,
                  movers: r.card.movers.map((m) => ({
                      listingId: m.listingId,
                      address: m.address,
                      direction: m.direction,
                      rank: m.rank,
                      previousRank: m.previousRank,
                      places: m.places,
                      score: m.score,
                      reason: m.reason,
                      fitBand: m.fitBand,
                  })),
              }
            : null,
        state: r.state,
        baselineMovedUtc: r.baselineMovedUtc ?? null,
        cardNotedSinceUtc: r.cardNotedSinceUtc ?? null,
        cardLapsesUtc: r.cardLapsesUtc ?? null,
        preferencesChangedSinceBaseline: r.preferencesChangedSinceBaseline ?? null,
    };
}

export function mapEngagement(e: Payload['engagement']): HomeFeedEngagementView | null {
    if (!e) return null;
    return {
        windowDays: e.windowDays,
        cap: e.cap,
        truncated: e.truncated,
        events: e.events.map((ev) => ({
            kind: ev.kind,
            occurredUtc: ev.occurredUtc,
            listingId: ev.listingId,
            quantity: ev.quantity ?? null,
            wildcardAxis: ev.wildcardAxis ?? null,
        })),
    };
}
